import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Document, Page, pdfjs } from "react-pdf";
import { ArrowBackOutlined, ArrowForwardOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import "moment/locale/id";
moment.locale("id");

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url,
).toString();

const validationSchema = Yup.object({
  judul_peringatan: Yup.string().required("Nama SOP wajib diisi"),
  id_karyawan: Yup.string().required("ID Karyawan wajib diisi"),
  file_peringatan: Yup.mixed()
    .required("File SOP wajib dipilih")
    .test("fileType", "Hanya mendukung PDF atau gambar", (value) => {
      return (
        value &&
        ["application/pdf", "image/jpeg", "image/png"].includes(value.type)
      );
    }),
});

const AddPeringatanModal = ({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const [filePreview, setFilePreview] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const { list_karyawan } = useSelector((state) => state.karyawan);

  const formik = useFormik({
    initialValues: {
      judul_peringatan: "",
      id_karyawan: "",
      file_peringatan: null,
      tanggal_berlaku: moment(),
      tanggal_berakhir: moment().add(3, "months"), // Default 3 bulan ke depan
    },
    validationSchema,
    onSubmit: (values) => {
      const reqData = {
        judul_peringatan: values.judul_peringatan,
        tanggal_berlaku: values.tanggal_berlaku.format("YYYY-MM-DD"),
        tanggal_berakhir: values.tanggal_berakhir.format("YYYY-MM-DD"),
        id_karyawan: values.id_karyawan,
        file_peringatan: values.file_peringatan,
      };
      onSubmit(reqData);
      formik.resetForm();
      setFilePreview(null);
      onClose();
    },
  });

  const handleTanggalBerlakuChange = (val) => {
    formik.setFieldValue("tanggal_berlaku", val);
    // Otomatis set tanggal berakhir 3 bulan setelahnya jika user mengubah tanggal berlaku
    if (val && val.isValid()) {
      formik.setFieldValue("tanggal_berakhir", moment(val).add(3, "months"));
    }
  };

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue("file_peringatan", file);
      setFilePreview(file);
      setNumPages(null);
      setPageNumber(1);
    }
  };
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber((prevPageNumber) => Math.max(1, prevPageNumber - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prevPageNumber) =>
      Math.min(numPages || 1, prevPageNumber + 1),
    );
  };

  const renderFilePreview = () => {
    if (!filePreview) return null;

    const fileType = filePreview.type;
    if (fileType === "application/pdf") {
      return (
        <div>
          <Document
            file={filePreview}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => console.error("PDF load error", error)}
          >
            <Page pageNumber={pageNumber} />
          </Document>
          {numPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              <Button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                size="small"
                color="warning"
              >
                <ArrowBackOutlined />
              </Button>

              <Typography style={{ margin: "0 10px", lineHeight: "2" }}>
                {pageNumber} / {numPages}
              </Typography>

              <Button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                size="small"
                color="warning"
              >
                <ArrowForwardOutlined />
              </Button>
            </div>
          )}
        </div>
      );
    } else if (fileType.startsWith("image/")) {
      return (
        <img
          src={URL.createObjectURL(filePreview)}
          alt="Preview"
          style={{ maxWidth: "100%", maxHeight: "300px", marginTop: 10 }}
        />
      );
    } else {
      return (
        <Typography variant="body2">
          Preview tidak tersedia untuk tipe ini.
        </Typography>
      );
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Tambah Peringatan Karyawan</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Judul Peringatan"
                name="judul_peringatan"
                value={formik.values.judul_peringatan}
                onChange={formik.handleChange}
                error={
                  formik.touched.judul_peringatan &&
                  Boolean(formik.errors.judul_peringatan)
                }
                helperText={
                  formik.touched.judul_peringatan &&
                  formik.errors.judul_peringatan
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={list_karyawan}
                getOptionLabel={(option) =>
                  `${option.id_karyawan} - ${option.nama_karyawan}`
                }
                value={
                  list_karyawan.find(
                    (item) => item.id_karyawan === formik.values.id_karyawan,
                  ) || null
                }
                onChange={(event, newValue) => {
                  formik.setFieldValue(
                    "id_karyawan",
                    newValue?.id_karyawan || "",
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Karyawan"
                    name="id_karyawan"
                    error={
                      formik.touched.id_karyawan &&
                      Boolean(formik.errors.id_karyawan)
                    }
                    helperText={
                      formik.touched.kode_jabatan && formik.errors.kode_jabatan
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  sx={{ width: "100%" }}
                  label="Tanggal Berlaku"
                  value={formik.values.tanggal_berlaku}
                  onChange={handleTanggalBerlakuChange} // Menggunakan fungsi handler baru
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={
                        formik.touched.tanggal_berlaku &&
                        Boolean(formik.errors.tanggal_berlaku)
                      }
                      helperText={
                        formik.touched.tanggal_berlaku &&
                        formik.errors.tanggal_berlaku
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  sx={{ width: "100%" }}
                  label="Tanggal Berakhir"
                  value={formik.values.tanggal_berakhir}
                  onChange={(val) =>
                    formik.setFieldValue("tanggal_berakhir", val)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      // Menampilkan pesan error validasi di UI
                      error={
                        formik.touched.tanggal_berakhir &&
                        Boolean(formik.errors.tanggal_berakhir)
                      }
                      helperText={
                        formik.touched.tanggal_berakhir &&
                        formik.errors.tanggal_berakhir
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" component="label" fullWidth>
                Pilih File Peringatan (PDF)
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {formik.touched.file_peringatan &&
                formik.errors.file_peringatan && (
                  <Typography color="error" variant="body2" mt={1}>
                    {formik.errors.file_peringatan}
                  </Typography>
                )}
            </Grid>
            <Grid item xs={12}>
              {renderFilePreview()}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="inherit"
            onClick={() => {
              onClose();
              setFilePreview(null);
              formik.resetForm();
            }}
            sx={{ fontSize: "14px", fontWeight: "bold", padding: "10px 20px" }}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.background.alt,
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: theme.palette.secondary[500],
              },
            }}
          >
            Simpan
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddPeringatanModal;
